/**
 * Абстракция геометрии фундаментальной области (Требование 6).
 *
 * Три операции, зависящие от кривизны поверхности (отождествление парных
 * сторон, середина/дуга геодезической и проекция в клип-координаты), сейчас
 * «зашиты» в гиперболическую реализацию (модель Пуанкаре). Интерфейс
 * {@link Geometry} инкапсулирует их, позволяя подключать евклидову и
 * сферическую реализации (задачи 3.2, 3.3) без изменения потребителей.
 *
 * Гиперболическая реализация — тонкая обёртка вокруг уже существующих чистых
 * функций (`mapOrderedPair`, `midpoint`, `geodesicArc`, композиция переносов
 * диска). Её поведение идентично прямым вызовам этих функций, поэтому
 * существующий код и тесты не затрагиваются.
 *
 * Требования: 6.3, 6.4
 */

import type { Complex, GeometryType, Isometry, Mobius } from './map-types.js';
import { apply, compose, determinant, invert } from './mobius.js';
import { normalize, translateFromOrigin, translateToOrigin } from './mobius.js';
import { mapOrderedPair } from './fuchsian-group.js';
import {
  midpoint as hyperbolicMidpoint,
  geodesicArc as hyperbolicGeodesicArc,
  distance as hyperbolicDistance,
} from './hyperbolic.js';
import {
  ONE,
  add,
  conjugate,
  divide,
  modulus,
  modulusSquared,
  multiply,
  negate,
  reciprocal,
  scale,
  subtract,
} from './complex.js';

/**
 * Геометрия фундаментальной области: набор операций, зависящих от кривизны
 * поверхности. Реализации: гиперболическая (модель Пуанкаре), евклидова и
 * сферическая (стереографическая проекция).
 */
export interface Geometry {
  /** Тип геометрии, определяющий реализацию операций. */
  readonly type: GeometryType;

  /**
   * Изометрия, отождествляющая упорядоченную пару точек стороны `(p0, p1)`
   * с парной стороной `(q0, q1)` (`p0 ↦ q0`, `p1 ↦ q1`).
   *
   * @param p0 первый конец исходной стороны
   * @param p1 второй конец исходной стороны
   * @param q0 образ `p0` на парной стороне
   * @param q1 образ `p1` на парной стороне
   */
  sidePairingIsometry(p0: Complex, p1: Complex, q0: Complex, q1: Complex): Isometry;

  /** Середина геодезического отрезка `[a, b]` (для триангуляции/подразбиения). */
  midpoint(a: Complex, b: Complex): Complex;

  /**
   * Точки ломаной аппроксимации геодезической дуги `[a, b]`
   * (для тесселяции границ и примитивов). Возвращает `segments + 1` точек.
   */
  geodesicArc(a: Complex, b: Complex, segments: number): Complex[];

  /**
   * Изометрия «захвата» `p0 ↦ p1` для навигации, сохраняющая модель.
   *
   * @param p0 исходная точка
   * @param p1 целевая точка
   */
  viewIsometry(p0: Complex, p1: Complex): Isometry;

  /** Проекция точки модели в клип-координаты (для вершинного шейдера). */
  project(z: Complex): Complex;

  /**
   * Применить обобщённую изометрию к точке модели с учётом обращения
   * ориентации (Требования 6, 8): для антиголоморфной изометрии аргумент
   * сначала сопрягается (`z ↦ z̄`), затем применяется действие линейной части
   * в метрике геометрии (аффинное — евклид, дробно-линейное SU(1,1) — гипербола,
   * дробно-линейное SU(2) — сфера).
   */
  apply(isometry: Isometry, z: Complex): Complex;

  /**
   * Композиция изометрий `a ∘ b` (сначала применяется `b`, затем `a`) с учётом
   * антиголоморфности. Если `a` обращает ориентацию, линейная часть `b`
   * сопрягается перед матричным произведением (тождество `conj(f_M(w)) =
   * f_{conj M}(conj w)`), а признак обращения ориентации результата равен
   * `a.orientationReversing XOR b.orientationReversing`.
   */
  compose(a: Isometry, b: Isometry): Isometry;

  /** Обратная изометрия (в метрике геометрии, с сохранением признака обращения). */
  invert(a: Isometry): Isometry;

  /**
   * Метрическое расстояние модели между точками (для выбора ближайшей плитки
   * при нормализации вида): гиперболическое, евклидово или сферическое.
   */
  distance(a: Complex, b: Complex): number;
}

/**
 * Матричные операции линейной части, зависящие от геометрии: голоморфное
 * действие `f_M`, композиция и инверсия матриц линейной части, нормализация к
 * инварианту модели. Обобщённые изометрии (с учётом обращения ориентации)
 * строятся из них функциями {@link applyIsometryWith}/{@link composeIsometryWith}/
 * {@link invertIsometryWith}.
 */
interface MatrixOps {
  /** Голоморфное действие линейной части `f_M(z)`. */
  apply(m: Mobius, z: Complex): Complex;
  /** Матричная композиция `first · second` (действует как `first(second(z))`). */
  compose(first: Mobius, second: Mobius): Mobius;
  /** Обращение матрицы линейной части. */
  invert(m: Mobius): Mobius;
  /** Нормализация матрицы к инварианту модели (компенсация числовой ошибки). */
  normalize(m: Mobius): Mobius;
}

/** Поэлементное сопряжение матрицы линейной части (`α ↦ ᾱ`, `β ↦ β̄`). */
function matrixConjugate(m: Mobius): Mobius {
  return { alpha: conjugate(m.alpha), beta: conjugate(m.beta) };
}

/** Матричные операции гиперболической геометрии (SU(1,1), модель Пуанкаре). */
const hyperbolicMatrixOps: MatrixOps = {
  apply,
  compose,
  invert,
  normalize,
};

/** Матричные операции евклидовой геометрии (аффинное движение `z ↦ α·z + β`). */
const euclideanMatrixOps: MatrixOps = {
  apply(m, z) {
    return add(multiply(m.alpha, z), m.beta);
  },
  compose(first, second) {
    // first(second(z)) = α₁(α₂ z + β₂) + β₁ = (α₁α₂) z + (α₁β₂ + β₁).
    return {
      alpha: multiply(first.alpha, second.alpha),
      beta: add(multiply(first.alpha, second.beta), first.beta),
    };
  },
  invert(m) {
    // w = α z + β ⇒ z = (1/α) w − β/α.
    const invAlpha = reciprocal(m.alpha);
    return { alpha: invAlpha, beta: negate(multiply(invAlpha, m.beta)) };
  },
  normalize(m) {
    // Привести линейную часть (поворот) к единичному модулю; перенос сохранить.
    const r = modulus(m.alpha);
    return r > 0 ? { alpha: scale(m.alpha, 1 / r), beta: m.beta } : m;
  },
};

/** Матричные операции сферической геометрии (SU(2), стереографическая проекция). */
const sphericalMatrixOps: MatrixOps = {
  apply(m, z) {
    const num = add(multiply(m.alpha, z), m.beta);
    const den = add(multiply(scale(conjugate(m.beta), -1), z), conjugate(m.alpha));
    return divide(num, den);
  },
  compose(first, second) {
    // Произведение матриц SU(2) [[α, β], [−β̄, ᾱ]].
    return {
      alpha: subtract(
        multiply(first.alpha, second.alpha),
        multiply(first.beta, conjugate(second.beta)),
      ),
      beta: add(
        multiply(first.alpha, second.beta),
        multiply(first.beta, conjugate(second.alpha)),
      ),
    };
  },
  invert(m) {
    // Обратная к унитарной SU(2) — сопряжённо-транспонированная: {ᾱ, −β}.
    return { alpha: conjugate(m.alpha), beta: negate(m.beta) };
  },
  normalize(m) {
    const n = Math.sqrt(modulusSquared(m.alpha) + modulusSquared(m.beta));
    return n > 0 ? { alpha: scale(m.alpha, 1 / n), beta: scale(m.beta, 1 / n) } : m;
  },
};

/** Тождественная изометрия (единичная матрица линейной части, без обращения). */
export function isometryIdentity(): Isometry {
  return { matrix: { alpha: { re: 1, im: 0 }, beta: { re: 0, im: 0 } }, orientationReversing: false };
}

/**
 * Привести значение к {@link Isometry}: матрица {@link Mobius} трактуется как
 * сохраняющая ориентацию изометрия (`orientationReversing = false`), а готовая
 * {@link Isometry} возвращается как есть.
 *
 * Позволяет рантайму (контроллер вида, LOD, слои рисунков/меток) принимать и
 * прежние матрицы (гиперболический ориентируемый путь, обратная совместимость),
 * и обобщённые изометрии, приводя их к единому представлению.
 */
export function asIsometry(x: Mobius | Isometry): Isometry {
  return 'matrix' in x ? x : { matrix: x, orientationReversing: false };
}

/** Применить обобщённую изометрию к точке (с сопряжением аргумента при обращении). */
function applyIsometryWith(ops: MatrixOps, iso: Isometry, z: Complex): Complex {
  return ops.apply(iso.matrix, iso.orientationReversing ? conjugate(z) : z);
}

/** Композиция обобщённых изометрий `a ∘ b` в заданной геометрии. */
function composeIsometryWith(ops: MatrixOps, a: Isometry, b: Isometry): Isometry {
  const inner = a.orientationReversing ? matrixConjugate(b.matrix) : b.matrix;
  return {
    matrix: ops.normalize(ops.compose(a.matrix, inner)),
    orientationReversing: a.orientationReversing !== b.orientationReversing,
  };
}

/** Обращение обобщённой изометрии в заданной геометрии. */
function invertIsometryWith(ops: MatrixOps, a: Isometry): Isometry {
  const inv = ops.invert(a.matrix);
  return {
    matrix: ops.normalize(a.orientationReversing ? matrixConjugate(inv) : inv),
    orientationReversing: a.orientationReversing,
  };
}

/**
 * Применить обобщённую изометрию (Требования 6, 8) к точке модели с учётом
 * флага обращения ориентации.
 *
 * Для антиголоморфной изометрии (`orientationReversing = true`) аргумент
 * сначала сопрягается (`z ↦ z̄`), затем применяется дробно-линейное действие
 * матрицы `M` — то есть `z ↦ M(z̄)` (Требование 8.2). Для сохраняющих
 * ориентацию изометрий матрица применяется напрямую (`z ↦ M(z)`).
 *
 * Используется дробно-линейное действие {@link apply} модели Пуанкаре
 * (гиперболическая/евклидова линейная часть). Сферическое действие SU(2) —
 * см. {@link applySphericalIsometry}, где флаг обращения ориентации учитывается
 * аналогично.
 *
 * @param isometry обобщённая изометрия (матрица линейной части + флаг обращения)
 * @param z точка модели
 */
export function applyIsometry(isometry: Isometry, z: Complex): Complex {
  const arg = isometry.orientationReversing ? conjugate(z) : z;
  return apply(isometry.matrix, arg);
}

/**
 * Определитель линейной части изометрии с учётом обращения ориентации
 * (Требования 8.2, 8.5).
 *
 * Матрица `M` — элемент SU(1,1)/SU(2) с определителем `≈ +1`. Антиголоморфная
 * изометрия `z ↦ M(z̄)` включает комплексное сопряжение, обращающее ориентацию,
 * поэтому определитель её (вещественной) линейной части равен `−det(M) < 0`
 * (отражение). Для сохраняющих ориентацию изометрий возвращается `+det(M) > 0`.
 *
 * @param isometry обобщённая изометрия
 * @returns определитель линейной части (строго отрицателен для обращающих ориентацию)
 */
export function isometryLinearDeterminant(isometry: Isometry): number {
  const linear = determinant(isometry.matrix);
  return isometry.orientationReversing ? -linear : linear;
}

/**
 * Гиперболическая геометрия (модель Пуанкаре, диск).
 *
 * Каждый метод делегирует уже существующим функциям геометрического ядра,
 * поэтому поведение полностью совпадает с прямыми вызовами:
 * - `sidePairingIsometry` → {@link mapOrderedPair} (сохраняющая ориентацию
 *   изометрия SU(1,1), `orientationReversing = false`);
 * - `midpoint` / `geodesicArc` → одноимённые функции из `hyperbolic.ts`;
 * - `viewIsometry` → композиция переносов диска
 *   `translateFromOrigin(p1) ∘ translateToOrigin(p0)` (`p0 ↦ p1`), совпадающая
 *   с `diskIsometryMapping` контроллера вида;
 * - `project` — тождественная проекция: точки диска уже являются
 *   клип-координатами модели.
 */
export const hyperbolicGeometry: Geometry = {
  type: 'hyperbolic',

  sidePairingIsometry(p0: Complex, p1: Complex, q0: Complex, q1: Complex): Isometry {
    return { matrix: mapOrderedPair(p0, p1, q0, q1), orientationReversing: false };
  },

  midpoint(a: Complex, b: Complex): Complex {
    return hyperbolicMidpoint(a, b);
  },

  geodesicArc(a: Complex, b: Complex, segments: number): Complex[] {
    return hyperbolicGeodesicArc(a, b, segments);
  },

  viewIsometry(p0: Complex, p1: Complex): Isometry {
    const matrix = normalize(compose(translateFromOrigin(p1), translateToOrigin(p0)));
    return { matrix, orientationReversing: false };
  },

  project(z: Complex): Complex {
    return { re: z.re, im: z.im };
  },

  apply(isometry: Isometry, z: Complex): Complex {
    return applyIsometryWith(hyperbolicMatrixOps, isometry, z);
  },

  compose(a: Isometry, b: Isometry): Isometry {
    return composeIsometryWith(hyperbolicMatrixOps, a, b);
  },

  invert(a: Isometry): Isometry {
    return invertIsometryWith(hyperbolicMatrixOps, a);
  },

  distance(a: Complex, b: Complex): number {
    return hyperbolicDistance(a, b);
  },
};

/**
 * Евклидова геометрия (плоская карта фундаментальной области).
 *
 * Изометрии — евклидовы движения, сохраняющие ориентацию: `z ↦ r·z + t`, где
 * `r = e^{iθ}` (поворот, единичный модуль при равных длинах парных сторон),
 * `t` — комплексный перенос. Пара `(α, β)` типа {@link Isometry.matrix}
 * переиспользуется как аффинное движение `α = r` (линейная часть, поворот),
 * `β = t` (перенос), то есть верхняя строка `2×2`-матрицы `[[α, β], [0, 1]]`,
 * действующей на `(z, 1)`.
 *
 * - `sidePairingIsometry` — жёсткое движение, переводящее упорядоченную пару
 *   `(p0, p1)` в `(q0, q1)`: поворот `r = (q1−q0)/(p1−p0)` выравнивает
 *   направление стороны, перенос `t = q0 − r·p0` совмещает концы
 *   (`p0 ↦ q0`, `p1 ↦ q1`) — перенос стороны в парную с заданной ориентацией;
 * - `midpoint` — арифметическое среднее `(a + b)/2` (середина отрезка);
 * - `geodesicArc` — прямые отрезки: `segments + 1` точек линейной интерполяции;
 * - `viewIsometry` — навигационный «захват» `p0 ↦ p1` как чистый перенос
 *   `z ↦ z + (p1 − p0)` (`α = 1`, `β = p1 − p0`);
 * - `project` — тождественная проекция (с масштабом): точки плоской карты уже
 *   являются клип-координатами модели.
 */
export const euclideanGeometry: Geometry = {
  type: 'euclidean',

  sidePairingIsometry(p0: Complex, p1: Complex, q0: Complex, q1: Complex): Isometry {
    const sourceDir = subtract(p1, p0);
    const targetDir = subtract(q1, q0);
    const rotationPart = divide(targetDir, sourceDir);
    const translation = subtract(q0, multiply(rotationPart, p0));
    return { matrix: { alpha: rotationPart, beta: translation }, orientationReversing: false };
  },

  midpoint(a: Complex, b: Complex): Complex {
    return scale(add(a, b), 0.5);
  },

  geodesicArc(a: Complex, b: Complex, segments: number): Complex[] {
    const count = Math.max(1, Math.floor(segments));
    const points: Complex[] = [];
    for (let i = 0; i <= count; i++) {
      const t = i / count;
      points.push({
        re: a.re + (b.re - a.re) * t,
        im: a.im + (b.im - a.im) * t,
      });
    }
    return points;
  },

  viewIsometry(p0: Complex, p1: Complex): Isometry {
    const translation = subtract(p1, p0);
    return { matrix: { alpha: { ...ONE }, beta: translation }, orientationReversing: false };
  },

  project(z: Complex): Complex {
    return { re: z.re, im: z.im };
  },

  apply(isometry: Isometry, z: Complex): Complex {
    return applyIsometryWith(euclideanMatrixOps, isometry, z);
  },

  compose(a: Isometry, b: Isometry): Isometry {
    return composeIsometryWith(euclideanMatrixOps, a, b);
  },

  invert(a: Isometry): Isometry {
    return invertIsometryWith(euclideanMatrixOps, a);
  },

  distance(a: Complex, b: Complex): number {
    return Math.hypot(a.re - b.re, a.im - b.im);
  },
};

// ---------------------------------------------------------------------------
// Сферическая геометрия: вспомогательная 3D-арифметика и стереографическая
// проекция сферы.
// ---------------------------------------------------------------------------

/** Точка/вектор в трёхмерном евклидовом пространстве (для модели сферы). */
interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Единичный кватернион `w + x·i + y·j + z·k` (вращение сферы). */
interface Quat {
  w: number;
  x: number;
  y: number;
  z: number;
}

/** Порог, ниже которого длина вектора/синуса угла считается вырожденной. */
const SPHERICAL_EPS = 1e-12;

function dot3(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

function cross3(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

function add3(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

function sub3(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function scale3(a: Vec3, s: number): Vec3 {
  return { x: a.x * s, y: a.y * s, z: a.z * s };
}

function length3(a: Vec3): number {
  return Math.hypot(a.x, a.y, a.z);
}

function normalize3(a: Vec3): Vec3 {
  const len = length3(a);
  return len > 0 ? scale3(a, 1 / len) : { x: 0, y: 0, z: 0 };
}

function clamp(value: number, lo: number, hi: number): number {
  return value < lo ? lo : value > hi ? hi : value;
}

/** Произвольный единичный вектор, ортогональный `v` (для вырожденных вращений). */
function anyPerpendicular(v: Vec3): Vec3 {
  const reference: Vec3 = Math.abs(v.x) < 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 };
  return normalize3(cross3(v, reference));
}

/**
 * Обратная стереографическая проекция: точка стереографической плоскости
 * `z = u + i·v` → точка единичной сферы `S²`. Проекция ведётся из северного
 * полюса `(0, 0, 1)`.
 */
function toSphere(z: Complex): Vec3 {
  const s = z.re * z.re + z.im * z.im;
  const d = 1 + s;
  return { x: (2 * z.re) / d, y: (2 * z.im) / d, z: (s - 1) / d };
}

/**
 * Прямая стереографическая проекция: точка единичной сферы → точка
 * стереографической плоскости. У северного полюса возвращает большое, но
 * конечное значение, чтобы избежать `Infinity`/`NaN`.
 */
function fromSphere(s: Vec3): Complex {
  const denom = 1 - s.z;
  if (Math.abs(denom) < SPHERICAL_EPS) {
    const scaleUp = 1 / SPHERICAL_EPS;
    return { re: s.x * scaleUp, im: s.y * scaleUp };
  }
  return { re: s.x / denom, im: s.y / denom };
}

/** Единичный кватернион вращения на угол `angle` вокруг единичной оси `axis`. */
function axisAngleQuat(axis: Vec3, angle: number): Quat {
  const half = angle / 2;
  const s = Math.sin(half);
  return { w: Math.cos(half), x: axis.x * s, y: axis.y * s, z: axis.z * s };
}

/** Произведение кватернионов `a ⊗ b` (сначала применяется вращение `b`). */
function quatMul(a: Quat, b: Quat): Quat {
  return {
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
  };
}

/** Повернуть вектор `v` кватернионом `q`: `v' = q · v · q⁻¹`. */
function rotateByQuat(q: Quat, v: Vec3): Vec3 {
  // t = 2·(q_vec × v); v' = v + q_w·t + q_vec × t
  const qv: Vec3 = { x: q.x, y: q.y, z: q.z };
  const t = scale3(cross3(qv, v), 2);
  return add3(add3(v, scale3(t, q.w)), cross3(qv, t));
}

/** Кватернион минимального вращения, переводящего единичный `u` в единичный `w`. */
function rotationBetween(u: Vec3, w: Vec3): Quat {
  const d = clamp(dot3(u, w), -1, 1);
  const axis = cross3(u, w);
  const axisLen = length3(axis);
  if (axisLen < SPHERICAL_EPS) {
    if (d > 0) {
      return { w: 1, x: 0, y: 0, z: 0 };
    }
    // Антиподы: поворот на π вокруг любой перпендикулярной оси.
    return axisAngleQuat(anyPerpendicular(u), Math.PI);
  }
  return axisAngleQuat(scale3(axis, 1 / axisLen), Math.atan2(axisLen, d));
}

/**
 * Кватернион вращения вокруг единичной оси `axis`, переводящего проекцию `from`
 * на плоскость, ортогональную оси, в проекцию `to` (совмещение азимута).
 */
function rotationAboutAxis(axis: Vec3, from: Vec3, to: Vec3): Quat {
  const fu = sub3(from, scale3(axis, dot3(from, axis)));
  const tu = sub3(to, scale3(axis, dot3(to, axis)));
  const fl = length3(fu);
  const tl = length3(tu);
  if (fl < SPHERICAL_EPS || tl < SPHERICAL_EPS) {
    return { w: 1, x: 0, y: 0, z: 0 };
  }
  const fn = scale3(fu, 1 / fl);
  const tn = scale3(tu, 1 / tl);
  const cosA = clamp(dot3(fn, tn), -1, 1);
  const sinA = dot3(cross3(fn, tn), axis);
  return axisAngleQuat(axis, Math.atan2(sinA, cosA));
}

/**
 * Кодировать единичный кватернион вращения как матрицу SU(2), хранимую в
 * {@link Mobius} (`α = q_w + i·q_z`, `β = −q_y + i·q_x`, `|α|² + |β|² = 1`).
 * Действие на стереографическую координату задаётся {@link applySphericalIsometry}.
 */
function quatToSU2(q: Quat): Mobius {
  return { alpha: { re: q.w, im: q.z }, beta: { re: -q.y, im: q.x } };
}

/**
 * Применить сферическую изометрию (SU(2)) к точке стереографической проекции:
 * `w ↦ (α·w + β) / (−β̄·w + ᾱ)`. Это дробно-линейное действие соответствует
 * вращению сферы, закодированному в {@link Isometry.matrix}.
 *
 * @param isometry сферическая изометрия (матрица SU(2))
 * @param z точка стереографической плоскости
 */
export function applySphericalIsometry(isometry: Isometry, z: Complex): Complex {
  const { alpha, beta } = isometry.matrix;
  const arg = isometry.orientationReversing ? conjugate(z) : z;
  const numerator = add(multiply(alpha, arg), beta);
  const denominator = add(multiply(scale(conjugate(beta), -1), arg), conjugate(alpha));
  return divide(numerator, denominator);
}

/**
 * Сферическая геометрия (единичная сфера `S²`, стереографическая проекция).
 *
 * Точки модели — стереографические проекции точек сферы. Изометрии — вращения
 * сферы, представленные элементами группы SU(2) (`|α|² + |β|² = 1`) и хранимые
 * в {@link Isometry.matrix}; их действие на стереографическую координату —
 * {@link applySphericalIsometry}. Геодезические — дуги больших окружностей,
 * спроецированные стереографически.
 *
 * - `sidePairingIsometry` — вращение сферы, отождествляющее упорядоченную пару
 *   `(p0, p1)` с парной `(q0, q1)` (`p0 ↦ q0`, `p1 ↦ q1`): композиция вращения,
 *   переводящего `p0` в `q0`, и вращения вокруг оси `q0`, совмещающего образ
 *   `p1` с `q1` (Требование 6.4, 6.6);
 * - `midpoint` — середина дуги большой окружности `[a, b]` (нормированная сумма
 *   точек сферы, спроецированная обратно);
 * - `geodesicArc` — `segments + 1` точек сферической (slerp) интерполяции вдоль
 *   дуги большой окружности (Требование 6.5);
 * - `viewIsometry` — навигационный «захват» `p0 ↦ p1` как минимальное вращение
 *   сферы, сохраняющее модель (Требование 6.7);
 * - `project` — ортографическая проекция точки сферы на экваториальный диск
 *   (`(x, y)`), дающая ограниченные клип-координаты.
 */
export const sphericalGeometry: Geometry = {
  type: 'spherical',

  sidePairingIsometry(p0: Complex, p1: Complex, q0: Complex, q1: Complex): Isometry {
    const sphereP0 = toSphere(p0);
    const sphereP1 = toSphere(p1);
    const sphereQ0 = toSphere(q0);
    const sphereQ1 = toSphere(q1);

    // Вращение, переводящее p0 в q0.
    const rotateP0ToQ0 = rotationBetween(sphereP0, sphereQ0);
    const imageOfP1 = rotateByQuat(rotateP0ToQ0, sphereP1);
    // Вращение вокруг оси q0, совмещающее образ p1 с q1.
    const alignP1 = rotationAboutAxis(sphereQ0, imageOfP1, sphereQ1);
    const combined = quatMul(alignP1, rotateP0ToQ0);

    return { matrix: quatToSU2(combined), orientationReversing: false };
  },

  midpoint(a: Complex, b: Complex): Complex {
    const sphereA = toSphere(a);
    const sphereB = toSphere(b);
    let mid = add3(sphereA, sphereB);
    if (length3(mid) < SPHERICAL_EPS) {
      // Антиподальные точки: середина не определена однозначно — берём точку
      // большой окружности, ортогональную обеим.
      mid = anyPerpendicular(sphereA);
    }
    return fromSphere(normalize3(mid));
  },

  geodesicArc(a: Complex, b: Complex, segments: number): Complex[] {
    const count = Math.max(1, Math.floor(segments));
    const sphereA = toSphere(a);
    const sphereB = toSphere(b);
    const omega = Math.acos(clamp(dot3(sphereA, sphereB), -1, 1));
    const sinOmega = Math.sin(omega);

    const points: Complex[] = [];
    if (sinOmega < 1e-9) {
      // Совпадающие или антиподальные концы: устойчивый фолбэк — линейная
      // интерполяция в стереографической плоскости.
      for (let i = 0; i <= count; i++) {
        const t = i / count;
        points.push({ re: a.re + (b.re - a.re) * t, im: a.im + (b.im - a.im) * t });
      }
      return points;
    }

    for (let i = 0; i <= count; i++) {
      const t = i / count;
      const s0 = Math.sin((1 - t) * omega) / sinOmega;
      const s1 = Math.sin(t * omega) / sinOmega;
      const point: Vec3 = {
        x: s0 * sphereA.x + s1 * sphereB.x,
        y: s0 * sphereA.y + s1 * sphereB.y,
        z: s0 * sphereA.z + s1 * sphereB.z,
      };
      points.push(fromSphere(point));
    }
    return points;
  },

  viewIsometry(p0: Complex, p1: Complex): Isometry {
    const grab = rotationBetween(toSphere(p0), toSphere(p1));
    return { matrix: quatToSU2(grab), orientationReversing: false };
  },

  project(z: Complex): Complex {
    const s = toSphere(z);
    return { re: s.x, im: s.y };
  },

  apply(isometry: Isometry, z: Complex): Complex {
    return applyIsometryWith(sphericalMatrixOps, isometry, z);
  },

  compose(a: Isometry, b: Isometry): Isometry {
    return composeIsometryWith(sphericalMatrixOps, a, b);
  },

  invert(a: Isometry): Isometry {
    return invertIsometryWith(sphericalMatrixOps, a);
  },

  distance(a: Complex, b: Complex): number {
    const sa = toSphere(a);
    const sb = toSphere(b);
    return Math.acos(clamp(dot3(sa, sb), -1, 1));
  },
};

/**
 * Выбрать реализацию {@link Geometry} по типу геометрии карты.
 *
 * Реализованы все три геометрии: гиперболическая (модель Пуанкаре), евклидова
 * (плоская карта) и сферическая (стереографическая проекция сферы, SU(2)).
 *
 * @param type тип геометрии (`MapConfig.geometryType`)
 */
export function createGeometry(type: GeometryType): Geometry {
  switch (type) {
    case 'hyperbolic':
      return hyperbolicGeometry;
    case 'euclidean':
      return euclideanGeometry;
    case 'spherical':
      return sphericalGeometry;
    default:
      throw new Error(`Geometry type '${type}' is not yet implemented`);
  }
}
